import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { identifyUser, resetUser } from '../utils/posthog';

export type AccessType = 'insight' | 'aviation' | 'guacamole' | 'admin';

interface User {
  email: string;
  id: string;
  stream_token: string;
  team: string;
  access?: {
    insight: boolean;
    aviation: boolean;
    guacamole: boolean;
    admin: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  logout: () => void;
  hasAccess: (module: AccessType) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const refreshUser = async () => {
    try {
      console.log('Refreshing user...', { path: location.pathname });
      
      // Skip session check on login page only, NOT on callback
      if (location.pathname === '/login') {
        console.log('Skipping session check on login page');
        setIsLoading(false);
        return;
      }

      // Fixed the URL to include /api prefix
      const response = await fetch('/api/user/session', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      console.log('Session response:', response.status);
      
      if (response.ok) {
        const userData = await response.json();
        console.log('User data:', userData);
        setUser(userData);
        sessionStorage.setItem('user', JSON.stringify(userData));
        
        // Identify user in PostHog
        identifyUser(userData);
      } else {
        console.log('No valid session, redirecting to login');
        setUser(null);
        sessionStorage.removeItem('user');
        // Reset PostHog user
        resetUser();
        if (location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
      sessionStorage.removeItem('user');
      // Reset PostHog user
      resetUser();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const logout = async () => {
    try {
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      // Reset PostHog user
      resetUser();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const hasAccess = (module: AccessType): boolean => {
    if (!user?.access) return false;
    return user.access[module] || false;
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, logout, hasAccess, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;